import _intersection from 'lodash/intersection';
import _pickBy from 'lodash/pickBy';

import nullSafeItems from '@lyearn/core/utils/array/nullSafeItems';
import { TFunction } from '@lyearn/i18n';
import {
  Calendar2,
  CheckmarkCircleOutlineLegacy,
  DoubleCheckmarkSquare,
  Hash,
  NormalText,
  PersonOutline,
} from '@lyearn/icons';
import { FilterOption } from '@/components/Filter/types';
import { isDateFilterEmpty } from '@/components/Form/components/Conditions/components/FilterValues/utils';
import {
  ContentPublishFailureReasonEnum,
  DateTagType,
  EntityTagInput,
  EntityTagsInput,
  EntityTagType,
  NumberTagTypeEnum,
  TagAvailableOnEnum,
  TagTypeEnum,
  TagUnion,
  TextTagTypeEnum,
  WorkspaceSubjects,
} from '@/types/schema';
import { getCurrentTimeZoneValue } from '@/utils/timezone';

import { ALL_SUBWORKSPACE_ID } from '../../workspaceSettings/const';
import {
  DateValue,
  MultiSelectValues,
  SelectedTagValues,
  TagInputProps,
  TagsMenuItem,
  UserTagValue,
} from '../types';
import getTagIcon from '../views/TagsManager/utils/getTagIcon';

export const DEFAULT_TAG_VALUE: TagInputProps = undefined;

/**
 *
 * @param tags : undefined
 * @returns undefined (backend will not update the tag field)
 *
 * @param tags: { tags: [], tagValues: {} }
 * @returns { set : adaptedTags || [] } (backend will update the tag field)
 *
 */

const ADAPT_TAG_KEYS = {
  [TagTypeEnum.Date]: 'dateValue',
  [TagTypeEnum.MultiSelect]: 'optionIdValues',
  [TagTypeEnum.SingleSelect]: 'optionIdValues',
  [TagTypeEnum.Number]: 'numberValue',
  [TagTypeEnum.Text]: 'textValue',
  [TagTypeEnum.User]: 'userIdValues',
};

const adaptTagValues = (tag: TagsMenuItem, tagValues: SelectedTagValues) => {
  if (!tagValues[tag.id]) {
    return undefined;
  }
  if (tag.type === TagTypeEnum.User) {
    const userIdValues = (tagValues[tag.id] as FilterOption[]).map((option) => option.value);
    return userIdValues.length ? userIdValues : undefined;
  }
  if (tag.type === TagTypeEnum.SingleSelect || tag.type === TagTypeEnum.MultiSelect) {
    const ids = (tagValues[tag.id] as MultiSelectValues)?.map((option) => option.value);
    return ids?.length ? ids : undefined;
  }
  if (tag.type === TagTypeEnum.Date) {
    const dateTagValue = tagValues[tag.id] as DateValue;
    const tagValue = tag as DateTagType;
    return isDateFilterEmpty(dateTagValue, tagValue.includeEndDate)
      ? undefined
      : { ...dateTagValue, timezone: getCurrentTimeZoneValue() };
  }

  return tagValues[tag.id];
};

export const adaptTagsOnSubmit = (
  tags?: TagInputProps,
  keepEmpty?: boolean,
): EntityTagsInput | null | undefined => {
  if (!tags) {
    return undefined;
  }

  const { tagValues } = tags;
  if (keepEmpty) {
    const adaptedTags: EntityTagInput[] = [];
    tags.tags.forEach((tag) => {
      const adaptedValue = adaptTagValues(tag, tagValues);
      adaptedTags.push({
        tagId: tag.id,
        type: tag.type,
        [ADAPT_TAG_KEYS[tag.type]]: adaptedValue,
      } as EntityTagInput);
    });

    return { set: adaptedTags };
  }

  if (!tagValues) {
    return undefined;
  }

  const adaptedTags: EntityTagInput[] = [];
  tags.tags.forEach((tag) => {
    if (tag.id in tagValues) {
      const adaptedValue = adaptTagValues(tag, tagValues);
      if (adaptedValue) {
        adaptedTags.push({
          tagId: tag.id,
          type: tag.type,
          [ADAPT_TAG_KEYS[tag.type]]: adaptedValue,
        } as EntityTagInput);
      }
    }
  });

  return { set: adaptedTags };
};

export const adaptTagsOnEdit = (tags?: EntityTagType[] | null) => {
  if (!tags) {
    return DEFAULT_TAG_VALUE;
  }

  const adaptedTags: TagsMenuItem[] = [];
  const adaptedTagValues: SelectedTagValues = {};
  const nullSafeTags = nullSafeItems(tags);

  nullSafeTags.forEach((tag) => {
    if (!tag.tag) {
      return;
    }
    const currentTag = getTagFilterOption(tag.tag);
    adaptedTags.push(currentTag);
    switch (currentTag.type) {
      case TagTypeEnum.Date:
        adaptedTagValues[currentTag.id] = tag.dateValue;
        break;
      case TagTypeEnum.SingleSelect:
      case TagTypeEnum.MultiSelect:
        adaptedTagValues[currentTag.id] = tag.options
          ? nullSafeItems(tag.options).map((option) => ({
              value: option.id,
              label: option.name,
            }))
          : undefined;
        break;
      case TagTypeEnum.Number:
        adaptedTagValues[currentTag.id] = tag.numberValue;
        break;
      case TagTypeEnum.Text:
        adaptedTagValues[currentTag.id] = tag.textValue;
        break;
      case TagTypeEnum.User:
        adaptedTagValues[currentTag.id] = tag.users
          ? (nullSafeItems(tag.users)?.map((user) => ({
              value: user.id,
              label: user.name,
              ...user,
            })) as FilterOption[])
          : undefined;
        break;
      default:
    }
  });

  return {
    tags: adaptedTags,
    tagValues: adaptedTagValues,
  };
};

export const areRequiredTagsEmpty = (tags: TagInputProps) => {
  if (!tags || !tags.tags?.length) {
    return false;
  }

  const { tagValues = {} } = tags;

  const tagWithNoValue = tags.tags.find((tag) => {
    if (!tag.settings.requiredTag) {
      return false;
    }

    if (tag.type === TagTypeEnum.MultiSelect || tag.type === TagTypeEnum.SingleSelect) {
      const currentValue = tagValues[tag.id] as MultiSelectValues;
      return !currentValue?.length;
    }

    if (tag.type === TagTypeEnum.User) {
      const currentValue = tagValues[tag.id] as UserTagValue;
      return !currentValue?.length;
    }

    return !tagValues[tag.id];
  });

  return !!tagWithNoValue;
};

export const getTagFilterOption = (tag: TagUnion): TagsMenuItem => {
  return {
    ...tag,
    value: tag.id,
    label: tag.name,
    Icon: () => getTagIcon(tag)!,
  };
};

export const getTagLabelAndIcon = (t: TFunction) => ({
  [TagTypeEnum.SingleSelect]: {
    label: t('Tags:Editor.TagTypeOptions.SingleSelect'),
    Icon: CheckmarkCircleOutlineLegacy,
  },
  [TagTypeEnum.MultiSelect]: {
    label: t('Tags:Editor.TagTypeOptions.MultiSelect'),
    Icon: DoubleCheckmarkSquare,
  },
  [TagTypeEnum.Text]: { label: t('Tags:Editor.TagTypeOptions.Text'), Icon: NormalText },
  [TagTypeEnum.Date]: { label: t('Common:Filter.Date'), Icon: Calendar2 },
  [TagTypeEnum.Number]: { label: t('Tags:Editor.TagTypeOptions.Number'), Icon: Hash },
  [TagTypeEnum.User]: { label: t('Common:User'), Icon: PersonOutline },
});

export const getTagAvailableOnLabel = (t: TFunction) => ({
  [TagAvailableOnEnum.Content]: t('Tags:Editor.AvailableOnOptions.Content'),
  [TagAvailableOnEnum.Event]: t('Tags:Editor.AvailableOnOptions.Event'),
  [TagAvailableOnEnum.User]: t('Tags:Editor.AvailableOnOptions.Learner'),
  [TagAvailableOnEnum.Okr]: t('Tags:Editor.AvailableOnOptions.Okr'),
  [TagAvailableOnEnum.Task]: t('Tags:Editor.AvailableOnOptions.Task'),
  [TagAvailableOnEnum.Consumption]: t('Tags:Editor.AvailableOnOptions.Consumption'),
});

export const getTextTagTypeLabel = (t: TFunction) => ({
  [TextTagTypeEnum.Long]: t('Tags:Editor.TextTagOptions.LongText'),
  [TextTagTypeEnum.Short]: t('Tags:Editor.TextTagOptions.ShortText'),
});

export const getNumberTagTypeLabel = (t: TFunction) => ({
  [NumberTagTypeEnum.Number]: t('Tags:Editor.NumberTagOptions.Number'),
  [NumberTagTypeEnum.Percentage]: t('Tags:Editor.NumberTagOptions.Percentage'),
  [NumberTagTypeEnum.Rupee]: t('Tags:Editor.NumberTagOptions.Rupee'),
  [NumberTagTypeEnum.Dollar]: t('Tags:Editor.NumberTagOptions.Dollar'),
  [NumberTagTypeEnum.Yen]: t('Tags:Editor.NumberTagOptions.Yen'),
  [NumberTagTypeEnum.Euro]: t('Tags:Editor.NumberTagOptions.Euro'),
  [NumberTagTypeEnum.Pound]: t('Tags:Editor.NumberTagOptions.Pound'),
});

export const getDateTagTypeLabel = (
  t: TFunction,
  includeEndDate?: boolean,
  includeTime?: boolean,
) => {
  let label = t('Common:Filter.Date');
  if (includeEndDate) {
    label += ` ${t('Tags:Editor.DateTagOptions.Range')}`;
  }
  if (includeTime) {
    label += `, ${t('Tags:Editor.DateTagOptions.Time')}`;
  }

  return label;
};

export const tagListWorkspaceSubjectMap = {
  [TagAvailableOnEnum.Content]: WorkspaceSubjects.Resource,
  [TagAvailableOnEnum.Event]: WorkspaceSubjects.Event,
  [TagAvailableOnEnum.User]: WorkspaceSubjects.Learner,
  // TODO: verify
  [TagAvailableOnEnum.Okr]: WorkspaceSubjects.OkrCycle,
  // todo check workspace config setting
  [TagAvailableOnEnum.Task]: WorkspaceSubjects.Learner,
  [TagAvailableOnEnum.Consumption]: WorkspaceSubjects.Resource,
};

export const REQUIRED_TAG_ERROR_CODE = ContentPublishFailureReasonEnum.MandatoryTagsNotAttached;

export function getFilteredTagsAndTagValuesByWorkspace({
  tags,
  tagValues,
  subWorkspaceIds,
}: {
  tags?: TagsMenuItem[];
  tagValues?: SelectedTagValues;
  subWorkspaceIds?: string[] | null;
}) {
  if (!subWorkspaceIds?.length || !tags?.length)
    return { tags: tags ?? [], tagValues: tagValues ?? {} };

  const filteredTags = isAllSubWorkpaces(subWorkspaceIds)
    ? tags
    : tags.filter((tag) => {
        return _intersection(tag.subWorkspaces, subWorkspaceIds).length > 0;
      });
  const filteredTagIds = new Set(filteredTags.map((tag) => tag.id));

  const filteredTagValues = _pickBy(tagValues ?? {}, (value, key) => filteredTagIds.has(key));

  return { tags: filteredTags, tagValues: filteredTagValues };
}

export function getFilteredTagsByWorkspaceForSubmit({
  entityTags,
  subWorkspaceIds,
}: {
  entityTags: EntityTagType[];
  subWorkspaceIds?: string[] | null;
}) {
  const adaptTags = adaptTagsOnEdit(entityTags);
  const { tags, tagValues } = getFilteredTagsAndTagValuesByWorkspace({
    tags: adaptTags?.tags,
    tagValues: adaptTags?.tagValues,
    subWorkspaceIds,
  });

  return adaptTagsOnSubmit({ tags, tagValues });
}

export const isAllSubWorkpaces = (subWorkspaceIds?: string[] | null) =>
  subWorkspaceIds?.indexOf(ALL_SUBWORKSPACE_ID) !== -1;

export const adaptSubWorkspaceIdsFilter = (subWorkspaceIds?: string[] | null) => {
  if (!subWorkspaceIds?.length) {
    return subWorkspaceIds;
  }

  if (isAllSubWorkpaces(subWorkspaceIds)) {
    return [];
  }

  return subWorkspaceIds;
};
